import SignInForm from "../components/core/SignInForm";
import { app } from "../App";
import { AppUser } from "../interfaces/IUsers";
import { findUserByEmail, findUserById } from "../utils/firestore";
import { FIREBASE_AUTH_ERROR_MESSAGES } from "../utils/dictionaries";
import { getAuth } from "firebase/auth";
import { hasRegistrationAccess } from "../utils/roleUtils";
import { signInFieldsList } from "../components/core/SignUp-SignIn/fieldsLists";
import { useLoggingActions } from "../hooks/useLogging";
import { useNavigationHelper } from "../hooks/useNavigationHelper";
import { useSignInWithEmailAndPassword } from "react-firebase-hooks/auth";
import { useState } from "react";

export default function SignIn() {
  const { goToContestsList } = useNavigationHelper();
  const [formData, setFormData] = useState<Record<string, any>>(
    signInFieldsList.reduce((acc, field) => ({ ...acc, [field.name]: "" }), {}),
  );
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [accessError, setAccessError] = useState<string | null>(null);
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(false);
  // eslint-disable-next-line
  const [signInWithEmailAndPassword, userCredentials, loading, error] =
    useSignInWithEmailAndPassword(getAuth(app));
  const { logUserLogin } = useLoggingActions();

  const validateFormData = () => {
    const errors: Record<string, string> = {};
    signInFieldsList.forEach((field) => {
      if (field.required && !formData[field.name]) {
        errors[field.name] = "To pole jest wymagane.";
      }
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSignIn = async () => {
    if (validateFormData()) {
      setIsCheckingPermissions(true);
      setAccessError(null);

      try {
        // Sprawdź uprawnienia PRZED logowaniem (po emailu)
        const userData = await findUserByEmail(formData.email);

        if (userData) {
          // Sprawdź nowy system ról
          const hasAccess = hasRegistrationAccess(userData);

          if (!hasAccess) {
            // Ustaw komunikat błędu bez logowania
            setAccessError(
              "Brak uprawnień do aplikacji."
            );
            return; // Stop here, do not attempt to log in
          }
        }
        // If user has access or doesn't exist in DB (will be handled by Root.tsx after login), proceed to login
        const userCredential = await signInWithEmailAndPassword(formData.email, formData.password);

        if (userCredential) {
          // 1. Pobierz pełne dane użytkownika z Firestore
          try {
            const userData = await findUserById(userCredential.user.uid);

            // 2. Logowanie sukcesu logowania
            try {
              await logUserLogin(userData, true);
            } catch (loggingError) {
              console.warn("Błąd logowania logowania (ignorowany):", loggingError);
            }
          } catch (error) {
            console.error("Błąd pobierania danych użytkownika:", error);

            // Logowanie błędu pobierania danych użytkownika
            try {
              const tempUser: AppUser = {
                id: userCredential.user.uid,
                email: userCredential.user.email || formData.email,
                firstName: "Unknown",
                lastName: "User",
                organizationName: "Unknown",
                consents: [],
                isActive: true,
                isFirstLogin: false,
                isInvoiceNeeded: false,
                phoneNumber: "",
              };
              await logUserLogin(tempUser, false, "Błąd pobierania danych użytkownika");
            } catch (loggingError) {
              console.warn("Błąd logowania błędu logowania (ignorowany):", loggingError);
            }
          }

          goToContestsList();
        }
      } catch (err: any) {
        console.error("Błąd logowania:", err);

        // Logowanie błędu logowania
        const errorMessage = err instanceof Error ? err.message : String(err);
        try {
          const tempUser: AppUser = {
            id: "temp",
            email: formData.email,
            firstName: "Unknown",
            lastName: "User",
            organizationName: "Unknown",
            consents: [],
            isActive: true,
            isFirstLogin: false,
            isInvoiceNeeded: false,
            phoneNumber: "",
          };
          await logUserLogin(tempUser, false, errorMessage);
        } catch (loggingError) {
          console.warn("Błąd logowania błędu logowania (ignorowany):", loggingError);
        }
      } finally {
        setIsCheckingPermissions(false);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prevData) => ({ ...prevData, [e.target.name]: e.target.value }));
    if (accessError) {
      setAccessError(null);
    }
  };

  return (
    <SignInForm
      formData={formData}
      formErrors={formErrors}
      onClickSignIn={handleSignIn}
      onChange={handleChange}
      loading={loading || isCheckingPermissions}
      error={
        accessError ||
        (error
          ? FIREBASE_AUTH_ERROR_MESSAGES[error.code as keyof typeof FIREBASE_AUTH_ERROR_MESSAGES]
          : undefined)
      }
    />
  );
}
